import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Package,
  Eye,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Pen,
  Search,
  EyeOff,
  X,
} from "lucide-react";
import {
  useNavigate,
  createSearchParams,
  useSearchParams,
} from "react-router-dom";
import { format } from "date-fns";
import { OrderDetailsModal } from "./OrderDetailsModal";

const OrdersDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const ordersPerPage = 5;
  const currentPage = parseInt(searchParams.get("page")) || 1;
  const urlSearchKeyword = searchParams.get("search") || "";

  useEffect(() => {
    setSearchKeyword(urlSearchKeyword);
  }, [urlSearchKeyword]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchKeyword !== urlSearchKeyword) {
        setSearchParams(
          createSearchParams({
            page: "1",
            search: searchKeyword,
          })
        );
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [searchKeyword, urlSearchKeyword, setSearchParams]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await axios.get("https://api.elza2.store/api/v1/order", {
          headers: {
            Authorization: localStorage.getItem("Auth"),
          },
          params: {
            page: currentPage,
            limit: ordersPerPage,
            search: urlSearchKeyword || "",
          },
        });
        setOrders(response.data.orders);
        setTotalPages(response.data.totalPages);
        setLoading(false);
      } catch (error) {
        setError(error.message);
        setLoading(false);
      }
    };

    fetchOrders();
  }, [currentPage, urlSearchKeyword]);

  const handleRefresh = () => {
    setSearchParams({});
    setOrders([]);
    setError(null);
  };

  const handlePageChange = (newPage) => {
    setSearchParams(
      createSearchParams({
        page: newPage.toString(),
        ...(urlSearchKeyword && { search: urlSearchKeyword }),
      })
    );
  };

  const navigateToOrderDetails = (orderId) => {
    navigate({
      pathname: `/order-details/${orderId}`,
      search: searchParams.toString(),
    });
  };

  // Rest of the component remains the same...
  const renderOrderStatus = (phase) => {
    const statusColors = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium ${
          statusColors[phase] || "bg-gray-100 text-gray-800"
        }`}
      >
        {phase}
      </span>
    );
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <RefreshCw className="animate-spin text-blue-500" size={48} />
      </div>
    );

  if (error)
    return (
      <div className="bg-red-50 p-4 rounded-lg text-center">
        <p className="text-red-600">{error}</p>
        <button
          onClick={handleRefresh}
          className="mt-4 bg-blue-500 text-white px-4 py-2 rounded"
        >
          Retry
        </button>
      </div>
    );

  return (
    <div className="bg-gray-50 min-h-screen p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center mb-4 sm:mb-0">
            <Package className="mr-2 sm:mr-4 text-blue-500" />
            Orders Dashboard
          </h1>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="relative w-full sm:w-auto">
              <input
                type="text"
                placeholder="Search orders..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="pl-10 pr-10 py-2 border rounded-lg w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              {searchKeyword && (
                <button
                  onClick={() => setSearchKeyword("")}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white shadow-md rounded-lg p-8 text-center">
            <Package className="mx-auto mb-4 text-gray-300" size={64} />
            <p className="text-gray-600">
              {urlSearchKeyword
                ? `No orders found for "${urlSearchKeyword}"`
                : "No orders found"}
            </p>
          </div>
        ) : (
          <div className="bg-white shadow-md rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {[
                      "/",
                      "Order ID",
                      "User Name",
                      "Date",
                      "Total Price",
                      "Status",
                      "Actions",
                    ].map((header) => (
                      <th
                        key={header}
                        className="p-4 text-left text-xs text-gray-500 uppercase tracking-wider"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr
                      key={order._id}
                      className="border-b hover:bg-gray-50 transition-colors"
                    >
                      <td className="p-4 text-sm text-gray-700">
                        {order.isViewed ? <Eye /> : <EyeOff />}
                      </td>
                      <td className="p-4 text-sm text-gray-700">
                        #{order._id.slice(-6)}
                      </td>
                      <td className="p-4 text-sm text-gray-700">
                        {order.ShippingAddress.firstName} {order.ShippingAddress.lastName}
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {format(new Date(order.createdAt), "MMMM dd, yyyy")}
                      </td>
                      <td className="p-4 font-semibold text-gray-800">
                        EGP {order.totalPrice?.toLocaleString()}
                      </td>
                      <td className="p-4">{renderOrderStatus(order.phase)}</td>
                      <td className="p-4">
                        <button
                          onClick={() => navigateToOrderDetails(order._id)}
                          className="text-blue-500 mx-1 hover:bg-blue-50 p-2 rounded-full transition"
                        >
                          <Eye size={20} />
                        </button>
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="text-blue-500 mx-1 bg-yellow-100 text-yellow-800 hover:bg-yellow-300 hover:text-black p-2 rounded-full transition"
                        >
                          <Pen size={20} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-gray-50 p-4 flex flex-col sm:flex-row justify-between items-center">
              <div className="text-sm text-gray-600 mb-4 sm:mb-0">
                {urlSearchKeyword ? (
                  <>
                    Search results for "{urlSearchKeyword}" - Page {currentPage}{" "}
                    of {totalPages}
                  </>
                ) : (
                  `Page ${currentPage} of ${totalPages}`
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                  disabled={currentPage === 1}
                  className="bg-white border rounded-lg p-2 disabled:opacity-50 hover:bg-gray-100 transition"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() =>
                    handlePageChange(Math.min(currentPage + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="bg-white border rounded-lg p-2 disabled:opacity-50 hover:bg-gray-100 transition"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </div>
  );
};

export default OrdersDashboard;
